import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileSignature, Loader2, Paperclip, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { normaliseEngagementLetter, type EngagementLetterState } from '../lib/engagementLetter';

const STORAGE_BUCKET = 'intake-documents';
const MAX_BYTES = 20 * 1024 * 1024; // 20 MB; engagement letters are tiny.

interface Props {
  matterId: string;
  isStaff: boolean;
  /** Called after staff upload/replace, so siblings (LodgementGate) re-evaluate. */
  onChange?: () => void;
}

/**
 * Engagement-letter compliance card. The model is deliberately binary: the
 * signed engagement-letter PDF is either on file or it isn't. Staff upload the
 * signed PDF; clients see whether it's on file and can download their own copy.
 * Lodgement is blocked until it's on file. MARA Code of Conduct 2021 reg. 6 + 14.
 */
export default function EngagementLetterPanel({ matterId, isStaff, onChange }: Props) {
  const [state, setState] = useState<EngagementLetterState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError(null);
    const { data, error: err } = await supabase
      .from('matters')
      .select('engagement_letter')
      .eq('id', matterId)
      .maybeSingle();
    if (err) {
      setError(err.message);
      setState(normaliseEngagementLetter(null));
      return;
    }
    const raw = (data as { engagement_letter: EngagementLetterState | null } | null)?.engagement_letter ?? null;
    setState(normaliseEngagementLetter(raw));
  }, [matterId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Resolve a fresh signed download URL whenever a document is on file.
  useEffect(() => {
    let cancelled = false;
    setDownloadUrl(null);
    const documentId = state?.document_id;
    if (!documentId) return;
    (async () => {
      const { data: doc } = await supabase
        .from('engagement_letter_documents' as never)
        .select('storage_path')
        .eq('id', documentId)
        .is('deleted_at', null)
        .maybeSingle();
      const path = (doc as { storage_path: string } | null)?.storage_path;
      if (!path) return;
      const { data: signed } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(path, 600);
      if (!cancelled && signed?.signedUrl) setDownloadUrl(signed.signedUrl);
    })();
    return () => {
      cancelled = true;
    };
  }, [state?.document_id]);

  const handleUpload = async (file: File) => {
    setError(null);
    if (file.type !== 'application/pdf') {
      setError('Engagement letters must be a PDF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File is over 20 MB.');
      return;
    }
    setUploading(true);
    try {
      const staffContactId = await resolveStaffContactId();
      const safeName = file.name.replace(/[^\w.\-+ ]/g, '_').slice(0, 200);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const storagePath = `${matterId}/engagement-letter/${ts}-${safeName}`;
      const sha256 = await sha256Hex(file); // tamper-detection, reg. 14(2)

      const { error: upErr } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(storagePath, file, { contentType: 'application/pdf', upsert: false });
      if (upErr) throw new Error(upErr.message);

      const documentId = crypto.randomUUID();
      const { error: insErr } = await supabase
        .from('engagement_letter_documents' as never)
        .insert({
          id: documentId,
          matter_id: matterId,
          uploaded_by_contact_id: staffContactId,
          storage_path: storagePath,
          filename: safeName,
          mime_type: 'application/pdf',
          size_bytes: file.size,
          content_sha256: sha256,
        } as never);
      if (insErr) {
        await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
        throw new Error(insErr.message);
      }

      const prev = normaliseEngagementLetter(state);
      const nowIso = new Date().toISOString();
      const next: EngagementLetterState = {
        ...prev,
        status: 'on_file',
        filed_at: nowIso,
        signed_at: prev.signed_at ?? nowIso,
        recorded_by_staff_id: staffContactId ?? prev.recorded_by_staff_id,
        document_id: documentId,
      };
      const { error: updErr } = await supabase
        .from('matters')
        .update({ engagement_letter: next } as never)
        .eq('id', matterId);
      if (updErr) throw new Error('Uploaded the file but failed to update the matter. Retry.');
      setState(next);
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const current = state ?? normaliseEngagementLetter(null);
  const onFile = current.status === 'on_file' && !!current.document_id;

  return (
    <div className="card overflow-hidden">
      <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center gap-2">
        <FileSignature size={14} className="text-slate-500" />
        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
          Engagement letter
        </h3>
        {!loading && (
          <span
            className={`inline-block h-2 w-2 rounded-full ${onFile ? 'bg-green-500' : 'bg-amber-500'}`}
            aria-hidden
          />
        )}
      </div>

      <div className="p-4 space-y-3 text-sm">
        {loading ? (
          <div className="text-slate-500">Loading...</div>
        ) : onFile ? (
          <p className="text-green-700 inline-flex items-center gap-1.5">
            <CheckCircle2 size={14} className="text-green-600" />
            On file{current.filed_at ? ` since ${relativeDate(current.filed_at)}` : ''}.
          </p>
        ) : (
          <p className="text-amber-700">
            Not uploaded. The signed engagement letter must be on file before lodgement (MARA Code
            of Conduct reg. 6 + 14).
          </p>
        )}

        {error && (
          <div className="text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Download link — staff always; clients only when on file (RLS). */}
        {onFile && downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#2CABEA] hover:underline"
          >
            <Paperclip size={14} /> Download signed engagement letter
          </a>
        )}

        {isStaff && !loading && (
          <div>
            <label
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold cursor-pointer ${
                uploading
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-[#0B2341] text-white hover:bg-[#0B2341]/90'
              }`}
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {onFile ? 'Replace PDF' : 'Upload signed PDF'}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                disabled={uploading}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                }}
              />
            </label>
          </div>
        )}

        {!isStaff && !onFile && !loading && (
          <p className="text-slate-500 text-xs">Your case manager will add this to your file.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function resolveStaffContactId(): Promise<string | null> {
  const { data: userData } = await supabase.auth.getUser();
  const authUserId = userData.user?.id;
  if (!authUserId) return null;
  const { data: staff } = await supabase
    .from('contacts')
    .select('id')
    .eq('user_id', authUserId)
    .eq('contact_type', 'staff')
    .is('deleted_at', null)
    .maybeSingle();
  return (staff as { id: string } | null)?.id ?? null;
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function relativeDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
